import { useState, useEffect, useCallback } from 'react';
import clsx from 'clsx';
import type { ChangelogEvent } from '@poi-planner/shared';
import { useTripsListStore } from '../store/tripsListStore';
import { api } from '../services/api';

const ACTION_CONFIG: Record<string, { icon: string; color: string }> = {
  create: { icon: 'add_circle', color: '#22c55e' },
  update: { icon: 'edit', color: '#3b82f6' },
  delete: { icon: 'delete', color: '#ef4444' },
  schedule: { icon: 'event', color: '#8b5cf6' },
  unschedule: { icon: 'event_busy', color: '#f59e0b' },
  reorder: { icon: 'swap_vert', color: '#6366f1' },
  sleep_change: { icon: 'hotel', color: '#ec4899' },
  undo: { icon: 'undo', color: '#94a3b8' },
  redo: { icon: 'redo', color: '#94a3b8' },
};

function formatRelativeTime(timestamp: string): string {
  const diff = Date.now() - new Date(timestamp).getTime();
  const seconds = Math.floor(diff / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export function ActivityFeed() {
  const [open, setOpen] = useState(false);
  const [events, setEvents] = useState<ChangelogEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const activeTripId = useTripsListStore((s) => s.activeTripId);

  const loadEvents = useCallback(async () => {
    if (!activeTripId) return;
    setLoading(true);
    try {
      const data = await api.getTripChangelog(activeTripId);
      setEvents(data);
    } catch {
      // silently fail
    }
    setLoading(false);
  }, [activeTripId]);

  useEffect(() => {
    if (open) loadEvents();
  }, [open, loadEvents]);

  return (
    <>
      <button
        onClick={() => setOpen(!open)}
        className={clsx('btn-icon', open && 'bg-slate-100')}
        title="Activity feed"
      >
        <span className="material-symbols-outlined text-lg">history</span>
      </button>

      {open && (
        <div className="fixed top-14 right-4 w-[360px] max-h-[calc(100vh-80px)] bg-white rounded-xl border border-border shadow-[0_8px_32px_rgba(0,0,0,0.12)] z-[100] flex flex-col overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-border">
            <h3 className="text-sm font-bold">Activity</h3>
            <button
              onClick={() => setOpen(false)}
              className="bg-transparent border-none p-0 cursor-pointer text-text-secondary flex"
            >
              <span className="material-symbols-outlined text-lg">close</span>
            </button>
          </div>

          <div className="custom-scrollbar flex-1 overflow-y-auto py-2">
            {loading && events.length === 0 ? (
              <div className="p-8 text-center text-text-muted text-[13px]">
                Loading...
              </div>
            ) : events.length === 0 ? (
              <div className="p-8 text-center text-text-muted text-[13px] flex flex-col items-center gap-2">
                <span className="material-symbols-outlined text-[32px] opacity-40">
                  history
                </span>
                <span>No activity yet</span>
              </div>
            ) : (
              events.map((event) => {
                const config = ACTION_CONFIG[event.action] ?? { icon: 'info', color: '#94a3b8' };
                return (
                  <div
                    key={event.id}
                    className="flex gap-3 px-5 py-2.5 transition-colors hover:bg-slate-50"
                  >
                    <div
                      className="size-7 rounded-full flex items-center justify-center shrink-0 mt-px"
                      style={{ background: `${config.color}15` }}
                    >
                      <span
                        className="material-symbols-outlined text-sm"
                        style={{ color: config.color }}
                      >
                        {config.icon}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[13px] text-text-primary leading-snug truncate">
                        {event.summary}
                      </p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-[11px] text-text-muted font-medium">
                          {event.user}
                        </span>
                        <span className="text-[11px] text-text-muted">
                          {formatRelativeTime(event.timestamp)}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </>
  );
}
